import { createSignal, onMount, onCleanup, Show, type JSX } from 'solid-js';
import { isServer } from 'solid-js/web';

// A tiny install nudge. We don't nag — it appears once, behind a dismiss
// key in localStorage. Native Capacitor builds will skip this entirely
// since beforeinstallprompt doesn't fire inside a WebView.
//
// Also responsible for registering the vite-plugin-pwa service worker
// (built from src/sw.ts). We register manually rather than via the plugin
// because SolidStart SSR's hydration timing makes auto-registration
// unreliable. Registration is gated on production builds (dev has
// devOptions.enabled: false) and skipped entirely during SSR.
//
// Note on the Chrome console message
//   "Banner not shown: beforeinstallpromptevent.preventDefault() called.
//    The page must call beforeinstallpromptevent.prompt() to show the
//    banner."
// is informational, not an error. Chrome logs it whenever a page calls
// preventDefault() on beforeinstallprompt — which is the exact thing we
// MUST do to suppress the native mini-infobar and show a custom UI
// instead. The deferred event is held until the user clicks Install, at
// which point we call prompt() in response to their gesture (the only
// time Chrome will display the install dialog).

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

const DISMISS_KEY = 'terp-track-install-dismissed';

function registerServiceWorker() {
  if (isServer || !('serviceWorker' in navigator)) return;
  // vite-plugin-pwa emits /sw.js at the site root in production.
  // Swallow registration errors — offline is a progressive enhancement,
  // not a hard requirement for the app to work.
  void navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => {});
}

export default function InstallPrompt(): JSX.Element {
  const [deferred, setDeferred] = createSignal<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = createSignal(false);

  onMount(() => {
    if (isServer) return;
    registerServiceWorker();

    // Always register the listener, even when the banner is dismissed,
    // so the deferred event is captured for any future install affordance
    // (e.g. a Settings → "Install app" button). beforeinstallprompt only
    // fires once per page load — if we skip the listener we lose the
    // event for the entire session.
    const dismissed = localStorage.getItem(DISMISS_KEY) === '1';
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      if (!dismissed) setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    onCleanup(() =>
      window.removeEventListener('beforeinstallprompt', handler),
    );
  });

  async function install() {
    const d = deferred();
    if (!d) return;
    await d.prompt();
    await d.userChoice;
    setDeferred(null);
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, '1');
  }

  function dismiss() {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, '1');
  }

  return (
    <Show when={visible()}>
      <div class="fixed bottom-4 inset-x-4 md:left-auto md:right-4 md:max-w-sm rounded-xl bg-brand-green text-brand-cream p-4 shadow-xl flex items-center gap-3 z-40">
        <div class="flex-1 text-sm">
          <p class="font-semibold">Install Terp Track</p>
          <p class="text-xs opacity-80">
            Track your sessions offline, with one-tap access from your home screen.
          </p>
        </div>
        <button type="button" onClick={install} class="rounded-full bg-brand-moss px-3 py-1.5 text-xs font-medium">
          Install
        </button>
        <button type="button" onClick={dismiss} aria-label="Dismiss" class="text-xs opacity-80">
          ✕
        </button>
      </div>
    </Show>
  );
}
